import { BackButton, MainButton, Text, View } from "@ds";
import {
	GalaxyButton,
	InviteFriendPopup,
	LangToggle,
	MainLayout,
	useConnectWallet,
	useDisconnectWallet,
	useIsWalletConnectionRestored,
} from "@shared";
import {
	AppLanguage,
	useAppActions,
	useGameSettingsActions,
	useOverlayActions,
	useSoundState,
} from "@store";
import { useTonWallet } from "@tonconnect/ui-react";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import chatIcon from "./assets/chat.svg";
import soundIcon from "./assets/sound.svg";
import { ArrowSvg } from "./components/Arrow";
import styles from "./Settings.module.scss";

interface NavigateButtonItemProps {
	title: string;
	onClick?: () => void;
}

const NavigateButtonItem = ({ title, onClick }: NavigateButtonItemProps) => {
	const { t } = useTranslation();
	return (
		<div className={styles.listItem} onClick={onClick}>
			<Text type="h3" className={styles.listItemText}>
				{t(title)}
			</Text>
			<div className={styles.arrow}>
				<ArrowSvg />
			</div>
		</div>
	);
};

export const Settings = () => {
	const [isInvitePopupOpen, setIsInvitePopupOpen] = useState(false);
	const muted = useSoundState();
	const { changeLanguage, changeSoundMuted } = useGameSettingsActions();
	const { connect } = useConnectWallet();
	const isWalletConnectionRestored = useIsWalletConnectionRestored();
	const { setIsLoading } = useAppActions();
	const { disconnect } = useDisconnectWallet();
	const wallet = useTonWallet();
	const { setOverlayQueue } = useOverlayActions();

	const toggleMute = useCallback(() => {
		changeSoundMuted(!muted);
	}, [muted, changeSoundMuted]);

	const navigate = useNavigate();

	const { t, i18n } = useTranslation();
	const language = i18n.language;

	const _toggleLocale = useCallback(() => {
		changeLanguage(
			language === AppLanguage.RU ? AppLanguage.EN : AppLanguage.RU,
		);
	}, [language, changeLanguage]);

	const handleSoonOverlayClick = () => {
		setOverlayQueue([{ type: "soon" }]);
	};

	const handleTermsOverlayClick = () => {
		setOverlayQueue([{ type: "termsAndPrivacy", payload: { type: "terms" } }]);
	};

	const handlePrivacyOverlayClick = () => {
		setOverlayQueue([
			{ type: "termsAndPrivacy", payload: { type: "privacy" } },
		]);
	};
	const handleWalletButtonClick = () => {
		if (wallet) {
			disconnect();
		} else {
			connect();
		}
	};

	useEffect(() => {
		setIsLoading(!isWalletConnectionRestored);
	}, [isWalletConnectionRestored, setIsLoading]);

	return (
		<MainLayout showInventory={false}>
			<BackButton onClick={() => navigate("/")} />
			<MainButton hidden />
			<InviteFriendPopup
				isOpen={isInvitePopupOpen}
				onClose={() => setIsInvitePopupOpen(false)}
			/>
			<View fadeIn className={styles.view}>
				<div className={styles.header}>
					<Text type="h1" center>
						{t("settings.title")}
					</Text>
				</div>
				<div className={styles.body}>
					<div className={styles.profileSettings}>
						<div
							onClick={toggleMute}
							className={classNames(styles.soundWrapper, {
								[styles.soundWrapper_active]: !muted,
							})}
						>
							<img src={soundIcon} />
						</div>
						<LangToggle />
						<div onClick={handleSoonOverlayClick} className={styles.support}>
							<img src={chatIcon} />
						</div>
					</div>
					<GalaxyButton
						withGlow={false}
						className={styles.saveButton}
						onClick={handleWalletButtonClick}
					>
						<Text type="body" center>
							{wallet ? t("settings.walletConnected") : t("settings.wallet")}
						</Text>
					</GalaxyButton>
					<section className={styles.listContainer}>
						<div className={styles.list}>
							<div
								className={styles.listItem}
								onClick={handleTermsOverlayClick}
							>
								<Text type="h3" className={styles.listItemText}>
									{t("settings.termsAndConditions")}
								</Text>
								<div className={styles.arrow}>
									<ArrowSvg />
								</div>
							</div>
							<div
								className={styles.listItem}
								onClick={handlePrivacyOverlayClick}
							>
								<Text type="h3" className={styles.listItemText}>
									{t("settings.privacyPolicy")}
								</Text>
								<div className={styles.arrow}>
									<ArrowSvg />
								</div>
							</div>
							<div className={styles.listItem} onClick={handleSoonOverlayClick}>
								<Text type="h3" className={styles.listItemText}>
									{t("settings.FAQ")}
								</Text>
								<div className={styles.arrow}>
									<ArrowSvg />
								</div>
							</div>
							<NavigateButtonItem
								title="settings.friends"
								onClick={() => {
									navigate("/friends");
								}}
							/>
						</div>
					</section>
				</div>
			</View>
		</MainLayout>
	);
};
